'use client';

import { TimelineContent } from '@/components/ui/timeline-animation';
import { VerticalCutReveal } from '@/components/ui/vertical-cut-reveal';
import { sitePath } from '@/lib/site-urls';
import { ArrowRight } from 'lucide-react';
import { useRef } from 'react';

const revealVariants = {
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    filter: 'blur(0px)',
    transition: { delay: i * 0.4, duration: 0.5 },
  }),
  hidden: { filter: 'blur(10px)', y: -20, opacity: 0 },
};

const textVariants = {
  visible: (i: number) => ({
    filter: 'blur(0px)',
    opacity: 1,
    transition: { delay: i * 0.3, duration: 0.7 },
  }),
  hidden: { filter: 'blur(10px)', opacity: 0 },
};

export function BlogHero() {
  const heroRef = useRef<HTMLDivElement>(null);

  return (
    <section className="w-full bg-neutral-50 px-4 py-16 sm:px-6 md:py-28 lg:px-8" ref={heroRef}>
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-6 flex items-center gap-2 text-sm font-medium text-neutral-500">
          <span className="text-neutral-900">✱</span>
          <TimelineContent
            as="span"
            animationNum={0}
            timelineRef={heroRef}
            customVariants={revealVariants}
          >
            INSIGHTS & GUIDES
          </TimelineContent>
        </div>

        <h1 className="mb-8 text-2xl font-semibold leading-[110%] text-neutral-900 sm:text-4xl md:text-5xl">
          <VerticalCutReveal splitBy="words" staggerDuration={0.1}>
            Practical software insights for South African businesses.
          </VerticalCutReveal>
        </h1>

        <TimelineContent
          as="p"
          animationNum={1}
          timelineRef={heroRef}
          customVariants={textVariants}
          className="mb-10 max-w-3xl text-base text-neutral-600 md:text-lg"
        >
          We are{' '}
          <span className="inline-block rounded-md border-2 border-dotted border-neutral-900 px-2 text-neutral-900">
            building
          </span>{' '}
          a library of guides on custom software, ERP, automation, and digital operations, written for
          decision-makers who need{' '}
          <span className="inline-block rounded-md border-2 border-dotted border-neutral-700 px-2 text-neutral-800">
            clarity
          </span>{' '}
          before they{' '}
          <span className="inline-block rounded-md border-2 border-dotted border-neutral-500 px-2 text-neutral-700">
            invest.
          </span>
        </TimelineContent>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <TimelineContent
            as="div"
            animationNum={2}
            timelineRef={heroRef}
            customVariants={textVariants}
          >
            <div className="text-xs font-medium uppercase text-neutral-900 sm:text-sm">ThinkinCode</div>
            <div className="text-xs font-semibold uppercase text-neutral-500 sm:text-sm">
              Software that works for your business
            </div>
          </TimelineContent>

          <TimelineContent
            as="a"
            animationNum={3}
            timelineRef={heroRef}
            customVariants={textVariants}
            href={sitePath('/services')}
            className="inline-flex h-12 cursor-pointer items-center gap-2 rounded-full bg-neutral-900 px-5 text-sm font-medium text-white shadow-lg shadow-neutral-900/20 transition hover:bg-neutral-800"
          >
            Explore our services
            <ArrowRight size={16} />
          </TimelineContent>
        </div>
      </div>
    </section>
  );
}
